import { useState } from "react";
import { Link, useParams } from "react-router";
import { useActivity } from "../api/queries";
import type { Job } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows, PageHead } from "../ui/Common";
import { JobPill } from "../ui/Pills";
import { activityMessages } from "./activity.i18n";

const KINDS: (Job["kind"] | "all")[] = ["all", "backup", "database", "share", "restore", "replication", "maintenance", "enrollment"];

export function ActivityPage() {
  const { orgId = "" } = useParams();
  const t = useT(activityMessages);
  const tc = useT(common);
  const f = useFormat();
  const jobs = useActivity(orgId);
  const [kind, setKind] = useState<Job["kind"] | "all">("all");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const rows = (jobs.data ?? []).filter((j) => (kind === "all" || j.kind === kind) && (!onlyProblems || j.status === "failed" || j.status === "warning"));

  return (
    <>
      <PageHead title={t.title} lede={t.lede} />
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div className="segmented" role="group" aria-label={t.kindFilter}>
          {KINDS.map((k) => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>{k === "all" ? t.all : tc.jobKind[k]}</button>
          ))}
        </div>
        <label className="check" htmlFor="act-problemas" style={{ alignItems: "center" }}>
          <input id="act-problemas" type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} style={{ marginTop: 0 }} />
          {t.onlyProblems}
        </label>
      </div>
      <section className="panel">
        {jobs.isPending ? <LoadingRows rows={10} /> : jobs.isError ? <div className="panel-body"><ErrorState error={jobs.error} /></div> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>{t.colStatus}</th><th>{t.colKind}</th><th>{t.colMachine}</th><th>{t.colStarted}</th><th className="right">{t.colDuration}</th><th className="right">{t.colData}</th><th>{t.colDetail}</th></tr></thead>
              <tbody>
                {rows.map((j) => (
                  <tr key={j.id}>
                    <td><JobPill status={j.status} /></td>
                    <td>{tc.jobKind[j.kind]}</td>
                    <td className="mono">{j.machineId ? <Link to={`/o/${orgId}/machines/${encodeURIComponent(j.machineId)}`}>{j.hostname}</Link> : <span className="muted">—</span>}</td>
                    <td className="num" style={{ whiteSpace: "nowrap" }}>{f.dateTime(j.startedAt)}</td>
                    <td className="right num">{j.endedAt ? f.duration(Math.max(1, Math.round((+new Date(j.endedAt) - +new Date(j.startedAt)) / 1000))) : j.progress !== null ? f.pct(j.progress) : "—"}</td>
                    <td className="right num">{j.bytes ? f.bytes(j.bytes) : "—"}</td>
                    <td className="ink-2" style={{ minWidth: 280 }}>{j.message}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7}><div className="empty">{t.empty}</div></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
